<template>
  <v-app>
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <v-expansion-panels v-model="panel">
            <v-expansion-panel>
              <v-expansion-panel-title color="blue-grey-lighten-4" class="panel-header">
                Supplier Ledger
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <!-- Date Filters -->
                <v-row class="py-2 pl-2">
                  <v-col lg="3" md="4" cols="12">
                    <v-date-input 
                      input-format="dd/mm/yyyy"
                      v-model="filters.from_date" 
                      label="From Date" 
                      density="compact" 
                      prepend-icon=""
                      prepend-inner-icon="mdi-calendar-start" 
                      variant="outlined" 
                      bg-color="grey-lighten-4" 
                      autocomplete="off"
                      clearable
                    />
                  </v-col>
                  <v-col lg="3" md="4" cols="12">
                    <v-date-input 
                      input-format="dd/mm/yyyy"
                      v-model="filters.to_date" 
                      label="To Date" 
                      density="compact" 
                      prepend-icon=""
                      prepend-inner-icon="mdi-calendar-end" 
                      variant="outlined" 
                      bg-color="grey-lighten-4" 
                      autocomplete="off"
                      clearable
                    />
                  </v-col>
                  <v-col lg="3" md="4" cols="12">
                    <v-select
                      v-model="filters.acc_account_head_id"
                      :items="accountHeadOptions"
                      item-title="text"
                      item-value="value"
                      label="All Account Head"
                      variant="outlined"
                      density="compact"
                      bg-color="grey-lighten-4"
                      prepend-inner-icon="mdi-account"
                      clearable
                      :loading="loadingAccountHeads"
                    />
                  </v-col>
                   <v-col lg="3" md="12" cols="12">
                    <div class="d-flex justify-start align-center flex-wrap">
                      <v-btn class="bg-primary mb-4" type="submit" @click="loadLedger" elevation="2" >
                        <v-icon left>mdi-magnify</v-icon> Search
                      </v-btn>
                    </div>
                  </v-col>
                 
                </v-row>

                <!-- Export Buttons -->
                <v-row v-if="ledgerData && ledgerData.length > 0" class="py-2 mt-2">
                  <v-col cols="12">
                    <div class="d-flex justify-center">
                      <v-btn-group variant="outlined" density="compact" size="x-small" divided>
                        <v-btn prepend-icon="mdi mdi-download" variant="flat" color="success" @click="exportToExcel" :disabled="!ledgerData">EXCEL</v-btn>
                        <v-btn prepend-icon="mdi mdi-download" variant="flat" color="red" @click="exportToPDF" :disabled="!ledgerData">PDF</v-btn>
                      </v-btn-group>
                    </div>
                  </v-col>
                </v-row>

                <!-- Ledger Report -->
                <v-card v-if="ledgerData && ledgerData.length > 0" class="mt-2">
                  <v-card-title class="text-center">
                    <div class="w-100">
                      <div class="text-h5 font-weight-bold">Supplier Ledger for: {{ formattedDate(filters.from_date) }} to {{ formattedDate(filters.to_date) }}</div>
                    </div>
                  </v-card-title>

                  <v-card-text class="pa-0">
                    <!-- Ledger Report - Individual Account Sections -->
                    <div class="ledger-report">
                      <div v-for="(account, index) in ledgerData" :key="account.id" class="ledger-account-section">
                        <!-- Account Header -->
                        <div class="account-header">
                          <strong>A/C Name :{{ account.account_group.name }}: {{ account.name }} - ({{ account.code }})</strong>
                        </div>

                        <!-- Ledger Table - Trial Balance Style -->
                        <div class="ledger-table-exact">
                          <!-- Table Header -->
                          <div class="ledger-header">
                            <div class="header-date">Date</div>
                            <div class="header-voucher">Voucher#</div>
                            <div class="header-particular">Particular</div>
                            <div class="header-amount">
                              <div class="header-debit">Debit</div>
                              <div class="header-credit">Credit</div>
                            </div>
                          </div>

                          <!-- Opening Balance Row -->
                          <div class="ledger-row opening-balance">
                            <div class="ledger-date">{{ formattedDate(filters.from_date) }}</div>
                            <div class="ledger-voucher">Opening</div>
                            <div class="ledger-particular">Opening Balance</div>
                            <div class="ledger-amount">
                              <div class="ledger-debit">{{ formatCurrency(account.opening_debit) }}</div>
                              <div class="ledger-credit">{{ formatCurrency(account.opening_credit) }}</div>
                            </div>
                          </div>

                          <!-- Transaction Rows -->
                          <div v-for="transaction in account.transactions" :key="transaction.acc_voucher_master_id" class="ledger-row">
                            <div class="ledger-date">{{ formattedDate(transaction.voucher_date) }}</div>
                            <div class="ledger-voucher">{{ transaction.code }}</div>
                            <div class="ledger-particular">
                              {{ transaction.remarks || 'Transaction' }}
                              <span v-if="transaction.cost_center && transaction.cost_center.name" class="cost-center">
                                ({{ transaction.cost_center.name }})
                              </span>
                            </div>
                            <div class="ledger-amount">
                              <div class="ledger-debit">{{ formatCurrency(transaction.debit_amount) }}</div>
                              <div class="ledger-credit">{{ formatCurrency(transaction.credit_amount) }}</div>
                            </div>
                          </div>

                          <!-- Account Total Row -->
                          <div class="ledger-row account-total">
                            <div class="ledger-date"></div>
                            <div class="ledger-voucher"></div>
                            <div class="ledger-particular"><strong>Account Total</strong></div>
                            <div class="ledger-amount">
                              <div class="ledger-debit"><strong>{{ formatCurrency(account.totals.during_debit) }}</strong></div>
                              <div class="ledger-credit"><strong>{{ formatCurrency(account.totals.during_credit) }}</strong></div>
                            </div>
                          </div>

                          <!-- Account Balance Row -->
                          <div class="ledger-row account-balance">
                            <div class="ledger-date"></div>
                            <div class="ledger-voucher"></div>
                            <div class="ledger-particular"><strong>Account Balance</strong></div>
                            <div class="ledger-amount">
                              <div class="ledger-debit"><strong>{{ formatCurrency(account.totals.closing_debit) }}</strong></div>
                              <div class="ledger-credit"><strong>{{ formatCurrency(account.totals.closing_credit) }}</strong></div>
                            </div>
                          </div>
                        </div>

                        <!-- Spacing between accounts -->
                        <div v-if="index < ledgerData.length - 1" class="account-spacing"></div>
                      </div>
                    </div>
                  </v-card-text>
                </v-card>

                <!-- No Data Message -->
                <v-card v-else-if="!loading && (!ledgerData || ledgerData.length === 0)" class="mt-2">
                  <v-card-text class="text-center py-8">
                    <v-icon size="64" color="grey">mdi-chart-line-variant</v-icon>
                    <div class="text-h6 mt-2">No Ledger Data Found</div>
                    <div class="text-body-2 text-grey">Please adjust your filters and try again</div>
                  </v-card-text>
                </v-card>

                <!-- Loading State -->
                <v-card v-if="loading" class="mt-2">
                  <v-card-text class="text-center py-8">
                    <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
                    <div class="text-h6 mt-2">Loading Ledger Report...</div>
                  </v-card-text>
                </v-card>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>

<script>
import formattedDate from '@/plugins/dateFormatter';
import { useStore } from 'vuex'

const store = useStore()
import { mapGetters } from 'vuex'

export default {
  name: 'LedgerReport',
  data() {
    return {
      panel: [0],
      loading: false,
      loadingAccountHeads: false,
      ledgerData: null,
      accountHeadOptions: [],
      filters: {
        from_date: new Date().toISOString().split('T')[0],
        to_date: new Date().toISOString().split('T')[0],
        acc_account_head_id: null
      }
    }
  },
  computed: {
    ...mapGetters(['getBranchId'])
  },
  mounted() {
    this.loadAccountHeads()
  },
  methods: {
    formattedDate,
    async loadAccountHeads() {
      this.loadingAccountHeads = true
      try {
         const headParams = {
          ledger_type: 'supplier'
        }
        const response = await this.$store.dispatch('getAccountHeads',headParams)
        // Flatten the tree structure to get all account heads
        const flatAccountHeads = this.flattenAccountHeads(response.data)
        this.accountHeadOptions = flatAccountHeads
          .filter(item => item.account_type === 'LEDGER') // Only show ledger accounts
          .map(item => ({
            text: `${item.code} - ${item.name}`,
            value: item.id
          }))
      } catch (error) {
        console.error('Error loading account heads:', error)
        this.$toast.error('Failed to load account heads')
      } finally {
        this.loadingAccountHeads = false
      }
    },

    flattenAccountHeads(accounts) {
      let result = []
      for (const account of accounts) {
        result.push(account)
        if (account.children && account.children.length > 0) {
          result = result.concat(this.flattenAccountHeads(account.children))
        }
      }
      return result
    },

    async loadLedger() {
      this.loading = true
      try {
        const params = {
          from_date: formattedDate(this.filters.from_date),
          to_date: formattedDate(this.filters.to_date),
          ledger_type:'supplier',
          acc_account_head_id: this.filters.acc_account_head_id
        }
        
        const response = await this.$store.dispatch('getLedger', params)
        if (response.success) {
          this.ledgerData = response.data
          this.$toast.success('Ledger report generated successfully')
        } else {
          this.$toast.error(response.message || 'Failed to generate ledger report')
        }
      } catch (error) {
        console.error('Error loading ledger:', error)
        this.$toast.error('Failed to load ledger report')
        this.ledgerData = null
      } finally {
        this.loading = false
      }
    },

    resetFilters() {
      this.filters = {
        from_date: new Date().toISOString().split('T')[0],
        to_date: new Date().toISOString().split('T')[0],
        acc_account_head_id: null
      }
      this.ledgerData = null
    },
    
    formatCurrency(amount) {
      if (!amount || amount === 0) return '0.00'
      return parseFloat(amount).toFixed(2)
    },

    exportToExcel() {
      if (!this.ledgerData || this.ledgerData.length === 0) return

      const data = [
        ['supplier Ledger'],
        [`Date: ${this.formattedDate(this.filters.from_date)} to ${this.formattedDate(this.filters.to_date)}`],
        []
      ]

      // Loop through each account
      this.ledgerData.forEach((account) => {
        // Account Header
        data.push([`A/C Name: ${account.account_group.name}: ${account.name} - (${account.code})`])
        data.push([])
        
        // Table Header
        data.push(['Date', 'Voucher#', 'Particular', 'Debit', 'Credit'])
        
        // Opening Balance
        data.push([
          this.formattedDate(this.filters.from_date),
          'Opening',
          'Opening Balance',
          this.formatCurrency(account.opening_debit),
          this.formatCurrency(account.opening_credit)
        ])

        // Transactions
        account.transactions.forEach((transaction) => {
          const particular = transaction.remarks || 'Transaction'
          const costCenter = transaction.cost_center && transaction.cost_center.name 
            ? ` (${transaction.cost_center.name})` 
            : ''
          
          data.push([
            this.formattedDate(transaction.voucher_date),
            transaction.code,
            particular + costCenter,
            this.formatCurrency(transaction.debit_amount),
            this.formatCurrency(transaction.credit_amount)
          ])
        })

        // Account Total
        data.push([
          '',
          '',
          'Account Total',
          this.formatCurrency(account.totals.during_debit),
          this.formatCurrency(account.totals.during_credit)
        ])

        // Account Balance
        data.push([
          '',
          '',
          'Account Balance',
          this.formatCurrency(account.totals.closing_debit),
          this.formatCurrency(account.totals.closing_credit)
        ])

        data.push([])
        data.push([])
      })

      this.$exportTableToExcel(data, 'supplier_ledger_' + this.$fileDate())
    },

    exportToPDF() {
      if (!this.ledgerData || this.ledgerData.length === 0) return

      const columns = ['Date', 'Voucher#', 'Particular', 'Debit', 'Credit']
      const rows = []

      // Loop through each account
      this.ledgerData.forEach((account, accountIndex) => {
        // Account Header (as a special row spanning all columns)
        rows.push([{
          content: `A/C Name: ${account.account_group.name}: ${account.name} - (${account.code})`,
          colSpan: 5,
          styles: { 
            fillColor: [245, 245, 245], 
            fontStyle: 'bold',
            halign: 'center'
          }
        }])
        
        // Opening Balance
        rows.push([
          this.formattedDate(this.filters.from_date),
          'Opening',
          'Opening Balance',
          this.formatCurrency(account.opening_debit),
          this.formatCurrency(account.opening_credit)
        ])

        // Transactions
        account.transactions.forEach((transaction) => {
          const particular = transaction.remarks || 'Transaction'
          const costCenter = transaction.cost_center && transaction.cost_center.name 
            ? ` (${transaction.cost_center.name})` 
            : ''
          
          rows.push([
            this.formattedDate(transaction.voucher_date),
            transaction.code,
            particular + costCenter,
            this.formatCurrency(transaction.debit_amount),
            this.formatCurrency(transaction.credit_amount)
          ])
        })

        // Account Total
        rows.push([
          '',
          '',
          'Account Total',
          this.formatCurrency(account.totals.during_debit),
          this.formatCurrency(account.totals.during_credit)
        ])

        // Account Balance
        rows.push([
          '',
          '',
          'Account Balance',
          this.formatCurrency(account.totals.closing_debit),
          this.formatCurrency(account.totals.closing_credit)
        ])

        // Add spacing between accounts (except for the last one)
        if (accountIndex < this.ledgerData.length - 1) {
          rows.push(['', '', '', '', ''])
        }
      })

      this.$exportTableToPDF(
        columns,
        rows,
        `supplier Ledger - ${this.formattedDate(this.filters.from_date)} to ${this.formattedDate(this.filters.to_date)}`,
        'supplier_ledger_' + this.$fileDate(),
        'portrait',
        {
          styles: { fontSize: 8, cellPadding: 2 },
          headStyles: { fontSize: 9, fillColor: [66, 139, 202], fontStyle: 'bold' },
          columnStyles: {
            0: { cellWidth: 25, halign: 'center' },  // Date
            1: { cellWidth: 25, halign: 'center' },  // Voucher#
            2: { cellWidth: 80, halign: 'right' },    // Particular
            3: { cellWidth: 30, halign: 'right' },   // Debit
            4: { cellWidth: 30, halign: 'right' }    // Credit
          },
          didParseCell: function(data) {
            // Bold style for Account Total and Account Balance rows
            if (data.cell.text[0] === 'Account Total' || data.cell.text[0] === 'Account Balance') {
              data.cell.styles.fontStyle = 'bold'
              data.cell.styles.fillColor = [240, 240, 240]
            }
          }
        }
      )
    }
  }
}
</script>

<style scoped>
.panel-header {
  font-weight: bold;
  font-size: 1.1rem;
}

/* Ledger Table - Trial Balance Style */
.ledger-account-section {
  margin-bottom: 20px;
}

.account-header {
  padding: 8px 12px;
  border-bottom: 1px solid #ddd;
  font-weight: bold;
  text-align: center;
  background-color: #f5f5f5;
}

.ledger-table-exact {
  font-family: 'Roboto', sans-serif;
  width: 100%;
  background-color: white;
  border: 2px solid #333;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 20px;
}

/* Header Styles */
.ledger-header {
  display: flex;
  align-items: center;
  font-weight: bold;
  background-color: #e3f2fd;
  border-bottom: 2px solid #1976d2;
  color: #1976d2;
}

.header-date {
  flex: 0 0 120px;
  padding: 8px 12px;
  font-size: 14px;
  text-align: center;
  border-right: 1px solid #ddd;
}

.header-voucher {
  flex: 0 0 100px;
  padding: 8px 12px;
  font-size: 14px;
  text-align: center;
  border-right: 1px solid #ddd;
}

.header-particular {
  flex: 1;
  padding: 8px 12px;
  font-size: 14px;
  text-align: left;
  border-right: 1px solid #ddd;
}

.header-amount {
  flex: 0 0 200px;
  padding: 8px 12px;
  font-size: 14px;
  text-align: center;
  border-right: 1px solid #ddd;
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-debit,
.header-credit {
  flex: 1;
  padding: 4px 8px;
  font-size: 12px;
  text-align: center;
  border-right: 1px solid #ddd;
}

.header-credit {
  border-right: none;
}

/* Row Styles */
.ledger-row {
  display: flex;
  align-items: center;
  min-height: 20px;
  border-bottom: 1px solid #e0e0e0;
  transition: background-color 0.2s ease;
}

.ledger-row:hover {
  background-color: #f8f9fa;
}

.ledger-row:nth-child(even) {
  background-color: #fafafa;
}

.ledger-row:nth-child(even):hover {
  background-color: #f0f0f0;
}

.ledger-date {
  flex: 0 0 120px;
  padding: 8px 12px;
  font-size: 14px;
  text-align: center;
  border-right: 1px solid #ddd;
}

.ledger-voucher {
  flex: 0 0 100px;
  padding: 8px 12px;
  font-size: 14px;
  text-align: center;
  border-right: 1px solid #ddd;
}

.ledger-particular {
  flex: 1;
  padding: 8px 12px;
  font-size: 14px;
  text-align: left;
  border-right: 1px solid #ddd;
}

.ledger-amount {
  flex: 0 0 200px;
  font-size: 14px;
  text-align: center;
  border-right: 1px solid #ddd;
  display: flex;
}

.ledger-debit,
.ledger-credit {
  flex: 1;
  padding: 8px 8px;
  font-size: 13px;
  text-align: right;
  border-right: 1px solid #ddd;
}

.ledger-credit {
  border-right: none;
}

.opening-balance {
  font-style: italic;
}

.account-total {
  border-top: 2px solid #000;
  background-color: #f0f0f0;
  font-weight: bold;
}

.account-balance {
  border-bottom: 2px solid #000;
  background-color: #f0f0f0;
  font-weight: bold;
}

.cost-center {
  font-size: 12px;
  color: #666;
  font-style: italic;
}

.account-spacing {
  height: 20px;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .ledger-date,
  .ledger-voucher,
  .ledger-particular,
  .ledger-debit,
  .ledger-credit {
    font-size: 10px;
    padding: 4px 6px;
  }
  
  .header-date,
  .header-voucher,
  .header-particular,
  .header-debit,
  .header-credit {
    font-size: 10px;
    padding: 4px 6px;
  }
}
</style>
